import { useCallback, useMemo, useState } from 'react';

import { resolveHitlTask } from '../services/hitlTasks.js';
import { buildApprovalDecision, parseCustomDecision } from '../utils/hitlDecision.js';

/**
 * Headless HITL decision core — no UI, no toasts, no i18n.
 *
 * Binds the decision builders (`utils/hitlDecision.js`) to the resolve service
 * so a consumer only supplies the task and the human input. The caller owns
 * all UX (toasts, dismissal, error copy); this hook only builds the payload,
 * calls resolve, tracks `resolving`, and invokes `onResolved` with the raw
 * outcome. Used by the in-chat gate card; the inbox slide-out shares the same
 * pure builders (`utils/hitlDecision.js`) but resolves through `useHitlTasks`
 * so it can refetch the list afterwards.
 *
 * @param {{ apiClient: import('axios').AxiosInstance,
 *   onResolved?: (result: object) => void | Promise<void> }} params
 */
export const useHitlDecision = ({ apiClient, onResolved } = {}) => {
  const [resolving, setResolving] = useState(false);

  const submit = useCallback(
    async ({ contractId, decision }) => {
      setResolving(true);
      try {
        const result = await resolveHitlTask(apiClient, { contractId, decision });
        await onResolved?.(result);
        return result;
      } finally {
        setResolving(false);
      }
    },
    [apiClient, onResolved],
  );

  const approve = useCallback(
    ({ task, comments }) =>
      submit({
        contractId: task.contractId,
        decision: buildApprovalDecision({
          gate: task?.resumeMatch?.gate,
          status: 'approved',
          comments,
        }),
      }),
    [submit],
  );

  const reject = useCallback(
    ({ task, comments }) =>
      submit({
        contractId: task.contractId,
        decision: buildApprovalDecision({
          gate: task?.resumeMatch?.gate,
          status: 'rejected',
          comments,
        }),
      }),
    [submit],
  );

  // Throws HitlDecisionJsonError on malformed JSON — the caller catches it to
  // show an "invalid JSON" message instead of firing a network request.
  const submitCustom = useCallback(
    ({ task, decisionJson }) =>
      submit({ contractId: task.contractId, decision: parseCustomDecision(decisionJson) }),
    [submit],
  );

  return useMemo(
    () => ({ resolving, approve, reject, submitCustom, submit }),
    [resolving, approve, reject, submitCustom, submit],
  );
};

export default useHitlDecision;
